// Shared page-scroll lock (menu drawer + bottom-slide modals can be open at the same time):
// counted so closing one layer does not free the scroll while another is still open.
// Locks native overflow on html + body (iOS needs both) and pauses Lenis (custom--smooth-scroll).
window.customScrollLock = window.customScrollLock || {
  count: 0,
  lock() {
    if (++this.count === 1) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.dispatchEvent(new Event('lenis:stop'));
    }
  },
  unlock() {
    if (this.count > 0 && --this.count === 0) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.dispatchEvent(new Event('lenis:start'));
    }
  },
};

// @ts-nocheck
class BottomSlideModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.focusableElements = [];
    this.lastFocusedElement = null;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.underlay && this.underlay.parentNode) {
      this.underlay.parentNode.removeChild(this.underlay);
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  render() {
    // Store the original content
    const content = this.innerHTML;
    this.innerHTML = '';

    // Create underlay
    this.underlay = document.createElement('div');
    this.underlay.className = 'modal-underlay';
    this.underlay.setAttribute('role', 'presentation');

    // Create modal container
    this.container = document.createElement('div');
    const containerClass = this.getAttribute('container-class') || 'modal-container';
    this.container.className = containerClass;
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-labelledby', 'modal-title');

    // Create content wrapper
    this.contentWrapper = document.createElement('div');
    this.contentWrapper.className = 'modal-content';
    this.contentWrapper.innerHTML = content;

    // Assemble the modal
    this.container.appendChild(this.contentWrapper);

    // Append to body (not to the component itself)
    document.body.appendChild(this.underlay);
    document.body.appendChild(this.container);
  }

  setupEventListeners() {
    const triggerClass = this.getAttribute('trigger-class');
    if (triggerClass) {
      this.triggers = document.querySelectorAll(`.${triggerClass}`);
      this.handleTriggerClick = this.open.bind(this);
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
      });
    }

    // Set up close button if close-button-class is provided
    const closeButtonClass = this.getAttribute('close-button-class');
    if (closeButtonClass) {
      this.closeButtons = this.container.querySelectorAll(`.${closeButtonClass}`);
      this.handleCloseBtnClick = this.close.bind(this);
      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', this.handleCloseBtnClick);
      });
    }

    this.handleUnderlayClick = this.close.bind(this);
    this.underlay.addEventListener('click', this.handleUnderlayClick);

    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  removeEventListeners() {
    if (this.triggers) {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
      });
    }
    if (this.underlay) {
      this.underlay.removeEventListener('click', this.handleUnderlayClick);
    }
    if (this.closeButtons) {
      this.closeButtons.forEach((btn) => {
        btn.removeEventListener('click', this.handleCloseBtnClick);
      });
    }
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  open(e) {
    if (e) {
      e.preventDefault();
    }
    this.isOpen = true;
    this.lastFocusedElement = document.activeElement;

    this.underlay.classList.add('active');
    this.container.classList.add('active');

    document.addEventListener('keydown', this.boundHandleKeydown);

    // Set up focus trap
    setTimeout(() => {
      this.setupFocusTrap();
      // Focus the first close button if it exists, otherwise focus the first focusable element
      if (this.closeButtons && this.closeButtons.length > 0) {
        this.closeButtons[0].focus();
      } else if (this.focusableElements.length > 0) {
        this.focusableElements[0].focus();
      }
    }, 100);

    // Refresh scroll animations after modal is visible
    setTimeout(() => {
      if (window.scrollAnimationManager) {
        window.scrollAnimationManager.refresh(this.container);
      }
    }, 100);

    // Prevent the page behind from scrolling (shared counted lock, see top of file)
    window.customScrollLock.lock();
  }

  close() {
    this.isOpen = false;

    this.underlay.classList.remove('active');
    this.container.classList.remove('active');

    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Restore page scroll
    window.customScrollLock.unlock();

    // Return focus to the element that opened the modal
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }

    if (e.key === 'Tab' && this.isOpen) {
      this.handleTab(e);
    }
  }

  setupFocusTrap() {
    // Get all focusable elements within the modal
    this.focusableElements = Array.from(
      this.container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
  }

  handleTab(e) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement =
      this.focusableElements[this.focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (e.shiftKey) {
      // Shift + Tab
      if (activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }
}

customElements.define('bottom-slide-modal', BottomSlideModal);
